import { Search, Plus, SlidersHorizontal, X, Loader2, UploadCloud } from 'lucide-react';
import { useAppStore } from '../../store/appStore';
import { useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../../lib/api';

const MODEL_PRESETS = ['Veo', 'Seedance', 'Midjourney', 'FLUX', 'Nano Banana', 'Sora'];

export function Header() {
  const { searchQuery, setSearchQuery, fetchAssets, filters, setFilters } = useAppStore();
  const navigate = useNavigate();
  const [localQuery, setLocalQuery] = useState(searchQuery);
  const [showFilter, setShowFilter] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploadedName, setUploadedName] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleSearch = useCallback(() => {
    setSearchQuery(localQuery);
    fetchAssets({ ...filters, query: localQuery || undefined, offset: 0 });
    // Medium#4: 详情页搜索后跳转回列表页（Gallery 渲染结果）
    if (window.location.pathname.startsWith('/asset/')) {
      navigate('/');
    }
  }, [localQuery, setSearchQuery, fetchAssets, filters, navigate]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleSearch();
  };

  const applyFilter = (patch: Partial<typeof filters>) => {
    const next = { ...filters, ...patch, offset: 0 };
    setFilters(next);
    fetchAssets(next);
  };

  const handleUpload = async (file: File) => {
    setUploading(true);
    setUploadError(null);
    setUploadedName(null);
    try {
      // 尝试从文件名提取 prompt/标题（无元数据时用文件名）
      const title = file.name.replace(/\.[^.]+$/, '');
      const res = await api.uploadAsset(file, { title });
      setUploadedName(res.data?.title || title);
      // 刷新列表
      fetchAssets({ ...filters, offset: 0 });
    } catch (e) {
      setUploadError((e as Error).message);
    } finally {
      setUploading(false);
    }
  };

  const onFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    files.forEach(handleUpload);
    e.target.value = '';
  };

  const clearFilters = () => {
    const next = { category: undefined, media_type: undefined, model_name: undefined, is_favorite: false, offset: 0 };
    setFilters(next);
    fetchAssets(next);
    setShowFilter(false);
  };

  return (
    <header className="h-16 border-b border-slate-800/50 bg-slate-900/20 backdrop-blur-sm flex items-center px-6 gap-4">
      {/* Search */}
      <div className="flex-1 max-w-xl relative">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
        <input
          type="text"
          value={localQuery}
          onChange={(e) => setLocalQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="搜索资产 — Prompt、标签、标题..."
          className="w-full pl-10 pr-4 py-2 bg-slate-800/50 border border-slate-700/50 rounded-lg text-sm text-slate-100 placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-violet-500/40 focus:border-violet-500/40 transition-all"
        />
      </div>

      {/* Actions */}
      <div className="flex items-center gap-2 relative">
        {/* 筛选按钮 + 弹层 */}
        <button
          className={`btn-ghost flex items-center gap-2 text-sm ${showFilter ? 'bg-violet-500/10 text-violet-300' : ''}`}
          onClick={() => setShowFilter(!showFilter)}
        >
          <SlidersHorizontal size={16} />
          筛选
          {(filters.category || filters.media_type || filters.model_name) && (
            <span className="w-1.5 h-1.5 rounded-full bg-violet-400" />
          )}
        </button>

        {showFilter && (
          <div className="absolute top-12 right-0 w-72 glass-card p-4 z-50 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-medium text-slate-200">高级筛选</h3>
              <button onClick={() => setShowFilter(false)} className="text-slate-500 hover:text-slate-300">
                <X size={16} />
              </button>
            </div>

            {/* 模型 */}
            <div>
              <p className="text-xs text-slate-500 mb-2">模型</p>
              <div className="flex flex-wrap gap-1.5">
                {MODEL_PRESETS.map((m) => (
                  <button
                    key={m}
                    onClick={() => applyFilter({ model_name: filters.model_name === m ? undefined : m })}
                    className={`px-2 py-1 text-xs rounded-md border transition-colors ${
                      filters.model_name === m
                        ? 'border-violet-500/50 bg-violet-500/10 text-violet-300'
                        : 'border-slate-700/50 text-slate-400 hover:border-slate-600'
                    }`}
                  >
                    {m}
                  </button>
                ))}
              </div>
            </div>

            {/* 媒体类型 */}
            <div>
              <p className="text-xs text-slate-500 mb-2">媒体类型</p>
              <div className="flex gap-1.5">
                {(['image', 'video', 'audio'] as const).map((t) => (
                  <button
                    key={t}
                    onClick={() => applyFilter({ media_type: filters.media_type === t ? undefined : t })}
                    className={`flex-1 px-2 py-1.5 text-xs rounded-md border transition-colors capitalize ${
                      filters.media_type === t
                        ? 'border-violet-500/50 bg-violet-500/10 text-violet-300'
                        : 'border-slate-700/50 text-slate-400 hover:border-slate-600'
                    }`}
                  >
                    {t}
                  </button>
                ))}
              </div>
            </div>

            <div className="pt-2 border-t border-slate-800/50 flex justify-between">
              <button onClick={clearFilters} className="text-xs text-slate-500 hover:text-slate-300">
                清除全部筛选
              </button>
              <button onClick={() => setShowFilter(false)} className="text-xs text-violet-400 hover:text-violet-300">
                完成
              </button>
            </div>
          </div>
        )}

        {/* 导入按钮（多文件上传） */}
        <button
          className="btn-primary flex items-center gap-2 text-sm"
          disabled={uploading}
          onClick={() => fileInputRef.current?.click()}
        >
          {uploading ? <Loader2 size={16} className="animate-spin" /> : <Plus size={16} />}
          {uploading ? '导入中...' : '导入'}
        </button>
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept="image/*,video/*,audio/*"
          className="hidden"
          onChange={onFileChange}
        />

        {uploadedName && (
          <div className="absolute top-12 right-0 glass-card p-3 z-50 flex items-center gap-2 text-xs text-emerald-300">
            <UploadCloud size={14} />
            已导入：{uploadedName}
            <button onClick={() => setUploadedName(null)} className="text-slate-500 hover:text-slate-300">
              <X size={12} />
            </button>
          </div>
        )}
        {uploadError && (
          <div className="absolute top-12 right-0 glass-card p-3 z-50 flex items-center gap-2 text-xs text-rose-300 max-w-xs">
            <X size={14} />
            {uploadError}
            <button onClick={() => setUploadError(null)} className="text-slate-500 hover:text-slate-300 ml-1">
              关闭
            </button>
          </div>
        )}
      </div>
    </header>
  );
}
